import React from 'react';
import HelpIcon from './HelpIcon';
import './MetricCard.css';

interface MetricCardProps {
  title: string;
  value: number | undefined | null | { impressions: number; clicks: number };
  color?: string;
  icon?: React.ReactNode;
  onHelpClick?: () => void;
  hideClicks?: boolean; // Hide clicks and CTR (for Broadcast TV, Radio, DOOH)
}

// Phase 1.3: Memoized to prevent unnecessary re-renders
const MetricCard: React.FC<MetricCardProps> = React.memo(({ title, value, color, icon, onHelpClick, hideClicks = false }) => {
  const formatNumber = (num: number | undefined | null): string => {
    if (num === null || num === undefined || isNaN(num)) {
      return '0';
    }
    return num.toLocaleString();
  };

  const renderValue = () => {
    if (typeof value === 'object' && value !== null && 'impressions' in value) {
      // If hideClicks is true, show only impressions (for Broadcast TV, Radio)
      if (hideClicks) {
        return (
          <div className="metric-card-dual-value">
            <div className="metric-primary">
              <b>{formatNumber(value.impressions)}</b>
              <span className="metric-label"> impressions</span>
            </div>
          </div>
        );
      }

      // Dual metric format: impressions / clicks / CTR
      const ctr = value.impressions > 0 ? ((value.clicks / value.impressions) * 100).toFixed(2) : '0.00';
      return (
        <div className="metric-card-dual-value">
          <div className="metric-primary">
            <b>{formatNumber(value.impressions)}</b>
            <span className="metric-label"> impressions</span>
          </div>
          <div className="metric-secondary">
            <b>{formatNumber(value.clicks)}</b>
            <span className="metric-label"> clicks</span>
          </div>
          <div className="metric-ctr">
            <span className="ctr-badge">{ctr}% CTR</span>
          </div>
        </div>
      );
    } else {
      // Legacy single value format
      return <b>{formatNumber(value as number)}</b>;
    }
  };

  return (
    <div className="metric-card" style={{ borderTopColor: color }}>
      <div className="metric-card-header">
        {icon && <div className="metric-card-icon">{icon}</div>}
        <h3 className="metric-card-title">
          {title}
          {onHelpClick && <HelpIcon onClick={onHelpClick} />}
        </h3>
      </div>
      <div className="metric-card-value">
        {renderValue()}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Phase 1.3: Custom comparison - only re-render if value actually changed
  return (
    prevProps.title === nextProps.title &&
    JSON.stringify(prevProps.value) === JSON.stringify(nextProps.value) &&
    prevProps.color === nextProps.color &&
    prevProps.hideClicks === nextProps.hideClicks
  );
});

export default MetricCard;
